import isEqual from 'lodash/isEqual';
import joi from 'joi';
import omit from 'lodash/omit';
import {
  $criteria,
  AccessRuleI18nAttributes,
  ActionTypes,
  EarningTransactionAction,
  ImpactMode,
  PeriodicityLimitationEnum,
  RuleAttributes,
  RuleI18nAttributes,
  RuleInput,
  RuleOptions,
  RuleType,
  periodicityLimitationSchema,
  ruleActionsSchema,
  ruleI18nAttributesSchema,
} from '@loyalty/shared-common/rule-api-client';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import { EventName, EventSource } from '@loyalty/shared-common';
import { EMPTY_EARNING_RULES_CRITERIA } from './EarningRulesTable';
import { EarningTransactionActionForm } from './EarningRuleTableRow';

export interface AccessRuleFormValue {
  criteria: any;
  i18nAttributes: AccessRuleI18nAttributes;
}

export interface EarningRuleFormValue {
  name: string;
  eventSource: EventSource;
  eventName: EventName;
  i18nAttributes: RuleI18nAttributes;
  criteria: Record<string, any>;
  stopTriggering: boolean;
  actions: EarningTransactionActionForm[];
  options: RuleOptions;
  deletedAt?: string;
  currency?: string;
  entity?: Partial<RuleAttributes>;
  type: RuleType.EARN;
}

export const earningRuleForm = joi.object<EarningRuleFormValue>({
  name: joi.string().required(),
  eventSource: joi
    .string()
    .valid(...Object.values(EventSource))
    .required(),
  eventName: joi.when('eventSource', {
    is: EventSource.BINARY,
    then: joi.string().required(),
    otherwise: joi.string().valid(...Object.values(EventName)),
  }),
  type: joi
    .string()
    .valid(...Object.values(RuleType))
    .required(),
  i18nAttributes: ruleI18nAttributesSchema,
  criteria: $criteria,
  actions: ruleActionsSchema,
  options: joi.object({ periodicityLimitation: periodicityLimitationSchema }).required(),
  deletedAt: joi.string().isoDate().optional().allow(null).empty(''),
});

export function earningRuleAttributesToFormValue(
  rule: Partial<RuleAttributes> | undefined,
  currencies: CurrencyAttributes[],
  languages: string[],
  timezone: string,
): EarningRuleFormValue {
  const firstActiveCurrency = currencies.find(currency => !currency.deletedAt);

  return {
    name: rule?.name || '',
    eventSource: rule?.eventSource || EventSource.QUALIFIO_ENGAGE,
    eventName: rule?.eventName || EventName.CAMPAIGN,
    criteria: rule?.criteria || EMPTY_EARNING_RULES_CRITERIA,
    i18nAttributes:
      rule?.i18nAttributes ||
      Object.fromEntries(languages.map(language => [language, { description: '' }])),
    actions: rule?.actions
      ? rule?.actions.filter(action => action.kind !== ActionTypes.STOP)
      : ([
          {
            kind: ActionTypes.TRANSACTION,
            impact: {
              mode: ImpactMode.FIXED_AMOUNT,
              value: null,
              currency: firstActiveCurrency?.id || '',
            },
          } as EarningTransactionAction,
        ] as any), // TODO improve typings
    stopTriggering: !!rule?.actions?.find(action => action.kind === ActionTypes.STOP),
    options: rule?.options || {
      periodicityLimitation: {
        enabled: false,
        periodicity: PeriodicityLimitationEnum.FOREVER,
        occurrence: 1,
        timezone,
      },
    },
    entity: rule,
    type: RuleType.EARN,
  };
}

export function formValueToEarningRuleInput({
  stopTriggering,
  actions,
  entity,
  ...formValue
}: EarningRuleFormValue): RuleInput {
  return {
    ...(entity as RuleAttributes),
    ...omit(formValue, ['entity']),
    type: RuleType.EARN,
    criteria:
      formValue.criteria && !isEqual(formValue.criteria, EMPTY_EARNING_RULES_CRITERIA)
        ? formValue.criteria
        : null,
    actions: [...actions, ...(stopTriggering ? [{ kind: ActionTypes.STOP }] : [])] as any, // TODO improve typings
  };
}
